import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import gsap from "gsap";
import { Heart, SlidersHorizontal } from "lucide-react";

import { EASE, prefersReducedMotion } from "../../animations/gsapAnimations";
import { SITE, whatsappHref } from "../../data/site";
import { toApiError } from "../../lib/http";
import { useGsapReveal, useGsapStagger } from "../../hooks/useGsapReveal";
import { useFocusTrap } from "../../hooks/useFocusTrap";
import { SORTS, useInventoryFilters } from "../../hooks/useInventoryFilters";
import Button from "../ui/Button";
import Section from "../ui/Section";
import { CmsHeading } from "../../cms/CmsHeading";
import Select from "../ui/Select";
import { InventoryCardSkeleton } from "../ui/Skeleton";
import FilterPanel from "./FilterPanel";
import InventoryCard from "./InventoryCard";

/* The inventory browser — the whole floor, filterable, on the home page.

   It used to be a page of its own at /cars. It is a section now because that
   is how people were using it: the cars are what the visitor came for, and
   sending them to a second page to see them is a click charged for nothing.
   The car's own page is still a page, and still where the link goes.

   The layout is the one a listing wants: the filters standing in a column of
   their own, and the grid beside them keeping its place while the query
   changes. Everything the panel sets lives in the URL, so a narrowed floor is
   a link. */

const SHORTLIST_KEY = "sm28:shortlist";

function InventorySection() {
  /* The floor, as the API narrowed it. Every control on the panel below is a
     search parameter, and every search parameter goes up with the request —
     so what the grid shows, what the URL says and what the visitor could send
     someone are one query rather than three that have to agree. */
  const {
    filters,
    facets,
    sort,
    results,
    loading,
    refreshing,
    error,
    retry,
    activeCount,
    setPrice,
    setSort,
    toggle,
    clearAll,
  } = useInventoryFilters();

  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  /* The shortlist is local only — a browsing convenience, not an account. */
  const [shortlist, setShortlist] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(SHORTLIST_KEY);
      return stored ? (JSON.parse(stored) as string[]) : [];
    } catch {
      return [];
    }
  });
  const [savedOnly, setSavedOnly] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SHORTLIST_KEY, JSON.stringify(shortlist));
    } catch {
      /* Private browsing and blocked storage are not errors worth surfacing. */
    }
  }, [shortlist]);

  const toggleSave = (id: string) =>
    setShortlist((current) =>
      current.includes(id)
        ? current.filter((saved) => saved !== id)
        : [...current, id],
    );

  const shown = savedOnly
    ? results.filter((vehicle) => shortlist.includes(vehicle.id))
    : results;

  const gridRef = useGsapStagger<HTMLUListElement>(
    "[data-card]",
    { y: 24, stagger: 0.05 },
    [shown.length],
  );

  /* The filters slide in from the edge they hang on, once, as the floor
     comes into view — a drawer opening beside the cars. */
  const filtersRef = useGsapReveal<HTMLElement>({
    x: -32,
    y: 0,
    start: "top 85%",
  });

  useFocusTrap(sheetRef, sheetOpen);

  /* The mobile sheet slides from the bottom edge, which is where the thumb
     already is. Visibility is toggled around the tween so a closed sheet is
     not a focus trap sitting off-screen. */
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    if (prefersReducedMotion()) {
      gsap.set(sheet, {
        visibility: sheetOpen ? "visible" : "hidden",
        yPercent: 0,
        opacity: 1,
      });
      return;
    }

    if (sheetOpen) {
      gsap.set(sheet, { visibility: "visible" });
      gsap.fromTo(
        sheet,
        { yPercent: 100 },
        { yPercent: 0, duration: 0.45, ease: EASE },
      );
      return;
    }

    gsap.to(sheet, {
      yPercent: 100,
      duration: 0.35,
      ease: EASE,
      onComplete: () => gsap.set(sheet, { visibility: "hidden" }),
    });
  }, [sheetOpen]);

  useLayoutEffect(() => {
    document.body.style.overflow = sheetOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sheetOpen]);

  const panel = (onClose?: () => void) => (
    <FilterPanel
      filters={filters}
      facets={facets}
      activeCount={activeCount}
      setPrice={setPrice}
      toggle={toggle}
      clearAll={clearAll}
      onClose={onClose}
    />
  );

  return (
    <Section id="cars" tone="surface" labelledBy="cars-title" bleed>
      {/* Full bleed, unlike every other section on the page: a listing is a
          workspace rather than an article, and the reading measure that suits
          a paragraph would leave the filters and the grid crowded into the
          middle of a wide screen with the band running out either side. Only
          the gutters hold it off the edge. */}
      <div className="px-4 sm:px-6 lg:px-8">
        <CmsHeading
          id="cars-title"
          eyebrow="Inventory"
          title="Every car on the floor"
          lede="Brand new, landed and ready. Narrow it by budget, by company, by the model under it or by colour — every price carries the figure the bank would quote beside it."
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-7">
          {/* ---------- the filters ---------- */}
          <aside ref={filtersRef} className="hidden lg:block">
            <div className="sticky top-[100px] flex max-h-[calc(100svh-128px)] flex-col">
              {panel()}
            </div>
          </aside>

          {/* ---------- the floor ---------- */}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-3 pb-5">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSheetOpen(true)}
                  className="flex items-center gap-2 rounded-full border border-line bg-raised px-4 py-2 text-[12.5px] font-medium text-platinum shadow-card transition hover:border-lime/45 hover:text-lime-deep lg:hidden"
                >
                  <SlidersHorizontal
                    aria-hidden="true"
                    strokeWidth={1.7}
                    className="size-4"
                  />
                  Filters
                  {activeCount > 0 ? (
                    <span className="grid size-4.5 place-items-center rounded-full bg-lime text-[10px] font-semibold text-void tabular-nums">
                      {activeCount}
                    </span>
                  ) : null}
                </button>

                <button
                  type="button"
                  onClick={() => setSavedOnly((value) => !value)}
                  aria-pressed={savedOnly}
                  disabled={shortlist.length === 0}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[12.5px] font-medium transition disabled:pointer-events-none disabled:opacity-45 ${
                    savedOnly
                      ? "border-lime bg-lime-ink text-lime-deep"
                      : "border-line bg-raised text-platinum shadow-card hover:border-lime/45 hover:text-lime-deep"
                  }`}
                >
                  <Heart
                    aria-hidden="true"
                    strokeWidth={1.7}
                    className="size-4"
                    fill={savedOnly ? "currentColor" : "none"}
                  />
                  Shortlist
                  {shortlist.length > 0 ? ` (${shortlist.length})` : ""}
                </button>

                <Select
                  label="Sort by"
                  hideLabel
                  value={sort}
                  onChange={(value) => setSort(value as typeof sort)}
                  options={SORTS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  className="min-w-40"
                />
              </div>
            </div>

            {loading ? (
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4">
                {Array.from({ length: 6 }, (_, index) => (
                  <li key={index}>
                    <InventoryCardSkeleton />
                  </li>
                ))}
              </ul>
            ) : error && shown.length === 0 ? (
              /* The floor is filtered by the API now, so an unreachable API
                 and an over-narrowed filter both arrive as no rows. They are
                 not the same thing to the person reading it: one is answered
                 by dropping a filter and the other by trying again, and
                 offering "clear all filters" to somebody whose connection has
                 dropped is advice that cannot work. */
              <div className="rounded-2xl border border-dashed border-line bg-raised px-8 py-20 text-center">
                <p className="font-display text-xl font-semibold text-platinum">
                  We could not load the floor
                </p>
                <p className="mx-auto mt-3 max-w-[52ch] text-[15px] leading-relaxed text-steel">
                  {toApiError(error).message}
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Button onClick={() => retry()}>Try again</Button>
                  <Button
                    variant="ghost"
                    href={whatsappHref(
                      `Hi ${SITE.name} ${SITE.suffix}, I am trying to see what you have on the floor.`,
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Talk to us on WhatsApp
                  </Button>
                </div>
              </div>
            ) : shown.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-line bg-raised px-8 py-20 text-center">
                <p className="font-display text-xl font-semibold text-platinum">
                  {savedOnly
                    ? "Nothing shortlisted yet"
                    : "No cars match these filters"}
                </p>
                <p className="mx-auto mt-3 max-w-[52ch] text-[15px] leading-relaxed text-steel">
                  {savedOnly
                    ? "Tap the heart on a car to keep it here while you look around."
                    : "Widen the budget or drop a filter — fresh stock lands every week, and we can source a specific variant on request."}
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  {savedOnly ? (
                    <Button onClick={() => setSavedOnly(false)}>
                      Back to the floor
                    </Button>
                  ) : (
                    <Button onClick={clearAll}>Clear all filters</Button>
                  )}
                  <Button
                    variant="ghost"
                    href={whatsappHref(
                      `Hi ${SITE.name} ${SITE.suffix}, I am looking for a car you do not have listed.`,
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Talk to us on WhatsApp
                  </Button>
                </div>
              </div>
            ) : (
              /* A changed filter is answered by the API, so there is a moment
                 between the click and the rows. The grid it replaces stays
                 where it is and dims rather than emptying — a listing that
                 blinked out on every tick would be worse than the wait. */
              <ul
                ref={gridRef}
                aria-busy={refreshing}
                className={`grid grid-cols-1 gap-4 transition-opacity duration-200 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 ${
                  refreshing ? "opacity-60" : "opacity-100"
                }`}
              >
                {shown.map((vehicle) => (
                  <li key={vehicle.id} data-card>
                    <InventoryCard
                      vehicle={vehicle}
                      savedIds={shortlist}
                      onToggleSave={toggleSave}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ---------- the filters, on a phone ----------
          Portalled to the body, and it has to be: PageTransition leaves a
          transform on the wrapper every page renders inside, and a transform
          is a containing block for anything position: fixed under it. Left
          in place, this sheet anchors to the foot of the whole page rather
          than the foot of the screen — and the focus trap then scrolls the
          visitor twenty thousand pixels down to find it. */}
      {createPortal(
        <>
          {sheetOpen ? (
            <button
              type="button"
              aria-label="Close filters"
              onClick={() => setSheetOpen(false)}
              className="fixed inset-0 z-50 bg-void/50 lg:hidden"
            />
          ) : null}

          <div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="Filter the floor"
            aria-hidden={!sheetOpen}
            className="invisible fixed inset-x-0 bottom-0 z-50 max-h-[86svh] overflow-y-auto rounded-t-3xl border-t border-line bg-elevated px-4 pt-5 pb-8 shadow-pop lg:hidden"
          >
            <span
              aria-hidden="true"
              className="absolute top-2.5 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-line"
            />

            {panel(() => setSheetOpen(false))}

            <Button
              className="mt-6 w-full"
              size="lg"
              onClick={() => setSheetOpen(false)}
              tabIndex={sheetOpen ? 0 : -1}
            >
              Show {shown.length} {shown.length === 1 ? "car" : "cars"}
            </Button>
          </div>
        </>,
        document.body,
      )}
    </Section>
  );
}

export default InventorySection;
